import { useEffect, useMemo, useState } from "react";
import "./Freelanceragenda.scss";
import { getFreelancerInProgressJobs } from "../../../../services/inprogressService";
import { getDateOnly } from "../../../../utils/dateUtils";
import EmptyState from "../../../../Components/EmptyState/EmptyState";

const DAYS_PT = ["DOM.", "SEG.", "TER.", "QUA.", "QUI.", "SEX.", "SÁB."];
const MONTHS_PT = ["jan.","fev.","mar.","abr.","mai.","jun.","jul.","ago.","set.","out.","nov.","dez."];
const MONTHS_FULL_PT = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diffToFri = (day - 5 + 7) % 7;
    d.setDate(d.getDate() - diffToFri);
    d.setHours(0, 0, 0, 0);
    return d;
}

function getWeekDays(weekStart) {
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
    });
}

function formatWeekRange(days) {
    const first = days[0];
    const last = days[days.length - 1];
    const fmt = (d) => `${String(d.getDate()).padStart(2, "0")} de ${MONTHS_PT[d.getMonth()]}`;
    return `${fmt(first)} a ${fmt(last)} de ${last.getFullYear()}`;
}

function formatFullDate(date) {
    return `${String(date.getDate()).padStart(2, "0")} de ${MONTHS_FULL_PT[date.getMonth()]} de ${date.getFullYear()}`;
}

function isSameDay(a, b) {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

function isSameWeek(a, b) {
    return a.getTime() === b.getTime();
}

function getLastDayOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfMonth(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
}

// Gera todas as células da grade do mês, respeitando o mesmo início de
// semana (sexta-feira) usado na visão semanal, incluindo os dias do
// mês anterior/seguinte necessários para fechar as linhas da grade.
function getMonthGridDays(monthCursor) {
    const firstOfMonth = startOfMonth(monthCursor);
    const lastOfMonth = getLastDayOfMonth(monthCursor);

    const gridStart = getWeekStart(firstOfMonth);
    const lastRowStart = getWeekStart(lastOfMonth);
    const gridEnd = new Date(lastRowStart);
    gridEnd.setDate(gridEnd.getDate() + 6);

    const totalDays = Math.round((gridEnd - gridStart) / 86400000) + 1;

    return Array.from({ length: totalDays }, (_, i) => {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        return d;
    });
}

function monthKey(date) {
    return date.getFullYear() * 12 + date.getMonth();
}

// ── Item de detalhe: label em cima, valor embaixo ──────────────────────
// Isso evita o problema de labels curtos + valores longos (ex: Vestimenta)
// quebrando de forma torta lado a lado.
function DetailItem({ icon, label, value, full }) {
    return (
        <div className={`fa-job-card__detail-item ${full ? "fa-job-card__detail-item--full" : ""}`}>
            <span className="fa-job-card__detail-label">
                <span className="fa-job-card__detail-icon">{icon}</span>
                {label}
            </span>
            <span className="fa-job-card__detail-value">{value}</span>
        </div>
    );
}

// ── Modal de detalhes da vaga ───────────────────────────────────────────
function JobDetailsModal({ job, onClose, onViewDetails }) {
    const handleGoToProgress = () => {
        onViewDetails();
        onClose();
    };

    return (
        <div className="fa-modal-backdrop" onClick={onClose}>
            <div className="fa-modal" onClick={(e) => e.stopPropagation()}>
                <button className="fa-modal__close" onClick={onClose} aria-label="Fechar detalhes">
                    ×
                </button>

                <div className="fa-modal__header">
                    <p className="fa-modal__eyebrow">Detalhes da vaga</p>
                    <h3 className="fa-modal__title">{job.title}</h3>
                </div>

                <div className="fa-modal__content">
                    <div className="fa-modal__summary">
                        <div className="fa-modal__summary-item">
                            <span className="fa-modal__summary-label">Horário</span>
                            <span className="fa-modal__summary-value">
                                {job.startTime?.slice(0, 5)} – {job.endTime?.slice(0, 5) || "?"}
                            </span>
                        </div>
                        <div className="fa-modal__summary-item">
                            <span className="fa-modal__summary-label">Local</span>
                            <span className="fa-modal__summary-value">{job.location}</span>
                        </div>
                        <div className="fa-modal__summary-item">
                            <span className="fa-modal__summary-label">Pagamento</span>
                            <span className="fa-modal__summary-value">
                                R$ {Number(job.payment).toFixed(2).replace(".", ",")}
                            </span>
                        </div>
                    </div>

                    <div className="fa-job-card__detail-grid">
                        {job.contactPerson && (
                            <DetailItem icon="👤" label="Responsável" value={job.contactPerson} />
                        )}
                        {job.breakTime && (
                            <DetailItem icon="☕" label="Intervalo" value={`${job.breakTime} min`} />
                        )}
                        {job.companyName && (
                            <DetailItem icon="🏢" label="Empresa" value={job.companyName} />
                        )}
                        {job.dressCode && (
                            <DetailItem icon="👔" label="Vestimenta" value={job.dressCode} full />
                        )}
                    </div>
                </div>

                <div className="fa-modal__footer">
                    <button className="fa-modal__secondary" onClick={onClose}>Fechar</button>
                    <button className="fa-modal__primary" onClick={handleGoToProgress}>
                        Ver andamento
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Modal com a lista de compromissos de um dia (visão mês) ─────────────
function DayJobsModal({ date, items, onClose, onSelectJob }) {
    return (
        <div className="fa-modal-backdrop" onClick={onClose}>
            <div className="fa-modal fa-modal--day" onClick={(e) => e.stopPropagation()}>
                <button className="fa-modal__close" onClick={onClose} aria-label="Fechar">
                    ×
                </button>

                <div className="fa-modal__header">
                    <p className="fa-modal__eyebrow">Compromissos do dia</p>
                    <h3 className="fa-modal__title">{formatFullDate(date)}</h3>
                </div>

                <div className="fa-day-jobs-list">
                    {items.map((item) => {
                        const job = item.job || item;
                        return (
                            <button
                                key={item.id}
                                className="fa-day-jobs-list__item"
                                onClick={() => onSelectJob(item)}
                            >
                                <span className="fa-day-jobs-list__dot" />
                                <span className="fa-day-jobs-list__info">
                                    <span className="fa-day-jobs-list__title">{job.title}</span>
                                    <span className="fa-day-jobs-list__time">
                                        {job.startTime?.slice(0, 5)} – {job.endTime?.slice(0, 5) || "?"}
                                    </span>
                                </span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Card de vaga aprovada na agenda ──────────────────────────────────────
function AgendaJobCard({ item, onViewDetails }) {
    const job = item.job || item;
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <div className="fa-job-card">
                <div className="fa-job-card__header">
                    <span className="fa-job-card__title">{job.title}</span>
                    <span className="fa-job-card__badge">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Aprovado
                    </span>
                </div>

                <div className="fa-job-card__meta">
                    <div className="fa-job-card__meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>{job.startTime?.slice(0, 5)} – {job.endTime?.slice(0, 5) || "?"}</span>
                    </div>
                    <div className="fa-job-card__meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                        </svg>
                        <span>{job.location}</span>
                    </div>
                </div>

                <div className="fa-job-card__footer">
                    <button className="fa-job-card__toggle" onClick={() => setIsModalOpen(true)}>
                        Ver detalhes
                        <svg
                            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <button className="fa-job-card__link" onClick={onViewDetails}>
                        Ver andamento
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                        </svg>
                    </button>
                </div>
            </div>

            {isModalOpen && (
                <JobDetailsModal
                    job={job}
                    onClose={() => setIsModalOpen(false)}
                    onViewDetails={onViewDetails}
                />
            )}
        </>
    );
}

// ── Ícones de alternância de layout ──────────────────────────────────────
function WeekViewIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="4" height="16" rx="1" />
            <rect x="10" y="4" width="4" height="16" rx="1" />
            <rect x="17" y="4" width="4" height="16" rx="1" />
        </svg>
    );
}

function MonthViewIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="3" y1="15" x2="21" y2="15" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <line x1="15" y1="3" x2="15" y2="21" />
        </svg>
    );
}

// ── Componente principal ──────────────────────────────────────────────────
export default function FreelancerAgenda({ onViewDetails }) {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // 🔥 MUDANÇA AQUI: começa com "month" em vez de "week"
    const [viewMode, setViewMode] = useState("month"); // <-- ALTERADO AQUI

    const [activeDaySheet, setActiveDaySheet] = useState(null); // { date, items }
    const [activeJob, setActiveJob] = useState(null); // job aberto via visão mês

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayWeekStart = getWeekStart(today);
    const todayMonthStart = startOfMonth(today);

    const [currentWeekStart, setCurrentWeekStart] = useState(todayWeekStart);
    const [monthCursor, setMonthCursor] = useState(todayMonthStart);

    const days = getWeekDays(currentWeekStart);
    const lastDayOfMonth = getLastDayOfMonth(days[days.length - 1]);
    const lastWeekStart = getWeekStart(lastDayOfMonth);
    const isOnTodayWeek = isSameWeek(currentWeekStart, todayWeekStart);
    const isOnLastWeek = currentWeekStart >= lastWeekStart;

    const isOnCurrentMonth = monthKey(monthCursor) === monthKey(todayMonthStart);
    const isOnLastMonth = monthKey(monthCursor) >= monthKey(todayMonthStart);

    const monthGridDays = useMemo(() => getMonthGridDays(monthCursor), [monthCursor]);
    const weekdayHeaderDays = monthGridDays.slice(0, 7);

    useEffect(() => {
        const fetchApproved = async () => {
            const userId = localStorage.getItem("userId");
            if (!userId) { setLoading(false); return; }
            try {
                const data = await getFreelancerInProgressJobs(userId);
                setApplications(data);
            } catch (err) {
                console.error("Erro ao buscar agenda:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchApproved();
    }, []);

    const getJobsForDay = (date) => {
        const dateStr = getDateOnly(date);
        return applications.filter((item) => {
            const job = item.job || item;
            return getDateOnly(job.date) === dateStr;
        });
    };

    const goToToday = () => {
        setCurrentWeekStart(new Date(todayWeekStart));
        setMonthCursor(new Date(todayMonthStart));
    };

    const goPrev = () => {
        if (viewMode === "week") {
            const prev = new Date(currentWeekStart);
            prev.setDate(prev.getDate() - 7);
            setCurrentWeekStart(prev);
        } else {
            setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1));
        }
    };

    const goNext = () => {
        if (viewMode === "week") {
            if (!isOnLastWeek) {
                const next = new Date(currentWeekStart);
                next.setDate(next.getDate() + 7);
                setCurrentWeekStart(next);
            }
        } else if (!isOnLastMonth) {
            setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1));
        }
    };

    const handleDayCellClick = (date, items) => {
        if (items.length === 0) return;

        if (items.length === 1) {
            setActiveJob(items[0]);
            return;
        }

        setActiveDaySheet({ date, items });
    };

    const isNextDisabled = viewMode === "week" ? isOnLastWeek : isOnLastMonth;
    const isTodayActive = viewMode === "week" ? isOnTodayWeek : isOnCurrentMonth;

    if (loading) {
        return <div className="fa-loading">Carregando agenda...</div>;
    }

    return (
        <div className="fa-wrapper">
            <div className="fa-card">

                {/* ── Header ── */}
                <div className="fa-header">
                    <div className="fa-title-group">
                        <div className="fa-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="fa-title">Minha Agenda</h1>
                            <p className="fa-subtitle">
                                {viewMode === "week"
                                    ? formatWeekRange(days)
                                    : `${MONTHS_FULL_PT[monthCursor.getMonth()]} de ${monthCursor.getFullYear()}`}
                            </p>
                        </div>
                    </div>

                    <div className="fa-controls">
                        <button
                            type="button"
                            className="fa-view-toggle"
                            onClick={() => setViewMode((v) => (v === "week" ? "month" : "week"))}
                            title={viewMode === "week" ? "Ver como calendário do mês" : "Ver como semana"}
                            aria-label="Alternar layout da agenda"
                        >
                            {viewMode === "week" ? <MonthViewIcon /> : <WeekViewIcon />}
                        </button>

                        <button
                            className={`fa-btn-hoje ${isTodayActive ? "fa-btn-hoje--active" : ""}`}
                            onClick={goToToday}
                        >
                            Hoje
                        </button>
                        <div className="fa-nav">
                            <button className="fa-nav-btn" onClick={goPrev} aria-label="Anterior">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <button
                                className="fa-nav-btn"
                                onClick={goNext}
                                disabled={isNextDisabled}
                                aria-label="Próximo"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Visão Semana ── */}
                {viewMode === "week" && (
                    <div className="fa-grid">
                        {days.map((date) => {
                            const isToday = isSameDay(date, today);
                            const dayJobs = getJobsForDay(date);
                            const dayName = DAYS_PT[date.getDay()];
                            const dayNum = date.getDate();
                            const monthName = MONTHS_PT[date.getMonth()];

                            return (
                                <div
                                    key={date.toISOString()}
                                    className={`fa-day-col ${isToday ? "fa-day-col--today" : ""} ${dayJobs.length > 0 ? "fa-day-col--has-jobs" : ""}`}
                                >
                                    <div className="fa-day-header">
                                        <span className="fa-day-name">{dayName}</span>
                                        <span className="fa-day-date">
                                            <strong>{dayNum}</strong> {monthName.toUpperCase()}
                                        </span>
                                        {dayJobs.length > 0 && (
                                            <span className="fa-day-count">{dayJobs.length}</span>
                                        )}
                                    </div>

                                    <div className="fa-day-body">
                                        {dayJobs.length === 0 ? (
                                            <div className="fa-empty">
                                                <div className="fa-empty-icon">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                        <circle cx="12" cy="12" r="9" />
                                                        <line x1="8" y1="12" x2="16" y2="12" />
                                                    </svg>
                                                </div>
                                                <span>Livre</span>
                                            </div>
                                        ) : (
                                            dayJobs.map((item) => (
                                                <AgendaJobCard key={item.id} item={item} onViewDetails={onViewDetails} />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── Visão Mês (mini-calendário) ── */}
                {viewMode === "month" && (
                    <div className="fa-mini-calendar-wrap">
                        <div className="fa-mini-weekday-row">
                            {weekdayHeaderDays.map((d) => (
                                <span key={d.toISOString()} className="fa-mini-weekday">
                                    {DAYS_PT[d.getDay()].replace(".", "")}
                                </span>
                            ))}
                        </div>

                        <div className="fa-mini-calendar">
                            {monthGridDays.map((date) => {
                                const isToday = isSameDay(date, today);
                                const isOutside = date.getMonth() !== monthCursor.getMonth();
                                const dayJobs = getJobsForDay(date);
                                const hasJobs = dayJobs.length > 0;

                                return (
                                    <button
                                        type="button"
                                        key={date.toISOString()}
                                        className={[
                                            "fa-mini-day",
                                            isOutside ? "fa-mini-day--outside" : "",
                                            isToday ? "fa-mini-day--today" : "",
                                            hasJobs ? "fa-mini-day--has-jobs" : "",
                                        ].join(" ").trim()}
                                        onClick={() => handleDayCellClick(date, dayJobs)}
                                        disabled={!hasJobs}
                                    >
                                        <span className="fa-mini-day__num">{date.getDate()}</span>
                                        {hasJobs && (
                                            dayJobs.length > 1 ? (
                                                <span className="fa-mini-day__count">{dayJobs.length}</span>
                                            ) : (
                                                <span className="fa-mini-day__dot" />
                                            )
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="fa-mini-legend">
                            <span className="fa-mini-legend__dot" />
                            Dia com compromisso — toque para ver os detalhes
                        </div>
                    </div>
                )}
            </div>

            {activeDaySheet && (
                <DayJobsModal
                    date={activeDaySheet.date}
                    items={activeDaySheet.items}
                    onClose={() => setActiveDaySheet(null)}
                    onSelectJob={(item) => {
                        setActiveDaySheet(null);
                        setActiveJob(item);
                    }}
                />
            )}

            {activeJob && (
                <JobDetailsModal
                    job={activeJob.job || activeJob}
                    onClose={() => setActiveJob(null)}
                    onViewDetails={onViewDetails}
                />
            )}
        </div>
    );
}